<template>
  <IonItem
    class="typeahead-item"
    :color="gray ? 'whitesmoke' : 'white'"
    button
    @click="disabled ? null : typeaheadModal?.$el?.present()"
    lines="full"
    :detail="false"
  >
    <div slot="start" class="flex items-center h-[60px] truncate">
      <ValidationIcon :valid-icon="icon" :errors="errors" :state="state"></ValidationIcon>
      <div class="font-medium text-gray-500">{{ title }}</div>
    </div>
    <div slot="end" class="flex items-center h-[60px] w-full flex-row-reverse">
      <IconButton
        size="small"
        @click="selectedLabel && clearable ? onClearClicked($event) : null"
        color="medium"
        :icon="selectedLabel && clearable ? 'icon-x' : 'icon-chevron-right'"
      ></IconButton>
      <div v-if="selectedLabel" class="w-full font-medium truncate text-right">
        <div class="text-gray-700 truncate">{{ selectedLabel }}</div>
        <div class="text-sm text-gray-500 truncate" v-if="selectedSubLabel">{{ selectedSubLabel }}</div>
      </div>
    </div>
  </IonItem>
  <IonModal
    class="typeahead-modal"
    ref="typeaheadModal"
    @ion-modal-will-present="onModalWillPresent()"
    @ion-modal-did-present="onModalDidPresent()"
    @ion-modal-will-dismiss="onModalWillDismiss()"
    @ion-modal-did-dismiss="onModalDidDismiss()"
    :backdrop-dismiss="true"
    :initial-breakpoint="1"
    :breakpoints="[0, 1]"
  >
    <IonContent class="ion-padding">
      <IonSearchbar
        ref="searchBarRef"
        @click="typeaheadModal?.$el.setCurrentBreakpoint(1)"
        :debounce="200"
        @ion-input="searchInput($event)"
      ></IonSearchbar>
      <IonRadioGroup v-model="vModel" allow-empty-selection :compare-with="compareWith">
        <IonList>
          <IonItem v-for="item of predictions">
            <IonRadio color="primary" :value="item.value" justify="space-between">
              <div :class="item.subLabel && item.subParts?.length ? 'text-sm' : 'text-base'">
                <span
                  v-for="part of item.mainParts"
                  class="font-medium"
                  :class="part.match ? 'text-cyan-700 bg-cyan-100 rounded' : 'text-gray-900'"
                  >{{ part.text }}</span
                >
              </div>
              <div class="text-xs">
                <span
                  v-for="part of item.subParts"
                  :class="part.match ? 'text-cyan-700 bg-cyan-100 rounded' : 'text-gray-700'"
                  >{{ part.text }}</span
                >
              </div>
            </IonRadio>
          </IonItem>
        </IonList>
      </IonRadioGroup>
    </IonContent>
  </IonModal>
</template>

<script setup lang="ts">
import {
  IonContent,
  IonItem,
  IonModal,
  IonList,
  IonRadio,
  IonRadioGroup,
  IonSearchbar,
  isPlatform,
  SearchbarCustomEvent,
} from '@ionic/vue';
import { computed, ref, useTemplateRef } from 'vue';
import { formatPredictions, Prediction } from './type-ahead';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import IconButton from '../IconButton.vue';
import ValidationIcon from '../ValidationIcon.vue';
import { FormControlError, FormControlState } from '@/composables/use-form-control';

const props = withDefaults(
  defineProps<{
    getOptions: (text: string) => Promise<any[]>;
    preformattedOptions?: boolean;
    title: string;
    compareWith?: (a: any, b: any) => boolean;
    labelKey?: string;
    secondaryLabelKey?: string;
    gray?: boolean;
    icon?: string;
    state?: FormControlState;
    errors?: FormControlError[];
    clearable?: boolean;
    skipSecondaryLabelMatch?: boolean;
    clearSearchOnDismiss?: boolean;
    disabled?: boolean;
  }>(),
  { clearable: true }
);

const typeaheadModal = useTemplateRef('typeaheadModal');

let lastEnteredInput: string;
const searchBarRef = ref();
const vModel = defineModel();
const selectedLabel = computed(() => {
  if (vModel.value) {
    if (props.labelKey) {
      return (vModel.value as any)[props.labelKey];
    } else {
      return vModel.value;
    }
  }
  return undefined;
});
const selectedSubLabel = computed(() => {
  if (vModel.value) {
    if (props.secondaryLabelKey) {
      return (vModel.value as any)[props.secondaryLabelKey];
    }
  }
  return undefined;
});
const predictions = ref<Prediction[]>([]);

function onClearClicked(ev: MouseEvent) {
  ev.stopPropagation();
  ev.preventDefault();
  vModel.value = undefined;
}

function onModalWillPresent() {
  if (isPlatform('hybrid')) {
    Keyboard.setResizeMode({ mode: KeyboardResize.None });
  }
  if (!predictions.value?.length) {
    props.getOptions('').then((options) => {
      predictions.value = props.preformattedOptions
        ? options
        : formatPredictions(options, '', {
            labelKey: props.labelKey,
            secondaryLabelKey: props.secondaryLabelKey,
            skipSecondaryLabelMatch: props.skipSecondaryLabelMatch,
          });
    });
  }
  if (lastEnteredInput && searchBarRef.value?.$el) {
    searchBarRef.value.$el.value = lastEnteredInput;
  }
}

function onModalWillDismiss() {
  if (isPlatform('hybrid')) {
    Keyboard.setResizeMode({ mode: KeyboardResize.Native });
  }
}

function onModalDidDismiss() {
  if (props.clearSearchOnDismiss) {
    clearSearch();
  }
}

function onModalDidPresent() {
  searchBarRef.value?.$el?.setFocus();
}

function searchInput(ev: SearchbarCustomEvent) {
  const input = ev.target.value || '';
  lastEnteredInput = input;
  props.getOptions(input).then((options) => {
    predictions.value = props.preformattedOptions
      ? options
      : formatPredictions(options, input, {
          labelKey: props.labelKey,
          secondaryLabelKey: props.secondaryLabelKey,
          skipSecondaryLabelMatch: props.skipSecondaryLabelMatch,
        });
  });
}

function clearSearch() {
  predictions.value = [];
  if (searchBarRef.value?.$el) {
    searchBarRef.value.$el.value = '';
  }
}
</script>

<style scoped>
ion-item.typeahead-item::part(native) {
  --border-width: 1px;
}
ion-item.typeahead-item {
  --padding-start: 0px;
}
</style>
